import { toApiError, type ApiError } from './client'

/**
 * POSTs `body` and reads the answer as a server-sent event stream: each
 * `data:` block is one JSON event, handed to `onEvent` as it arrives. The
 * browser's EventSource cannot POST, and axios cannot read a body while it is
 * still arriving, so this is the one place the app uses `fetch`.
 *
 * A refusal before the stream starts (no session, no access, invalid input)
 * comes back as the API's usual error envelope: it rejects with the same
 * `ApiError` every other call does, and a lost session redirects the same way.
 * Resolves when the server closes the stream; an `AbortSignal` stops reading.
 */
export const streamEvents = async <TEvent>(
  path: string,
  body: unknown,
  onEvent: (event: TEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const url = `/api${path}`
  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    })
  } catch {
    if (signal?.aborted) return
    throw toApiError(undefined, 0, url)
  }

  const isStream = response.headers.get('content-type')?.includes('text/event-stream') === true
  if (!response.ok || !isStream || !response.body) throw toApiError(await envelopeOf(response), response.status, url)

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  try {
    for (;;) {
      const chunk = await readChunk(reader, url, signal)
      if (chunk === null) break
      buffer += chunk
      // Events are separated by a blank line; the last piece may still be arriving.
      const blocks = buffer.split(/\r?\n\r?\n/)
      buffer = blocks.pop() ?? ''
      for (const block of blocks) {
        const event = parseBlock<TEvent>(block)
        if (event !== undefined) onEvent(event)
      }
    }
    const last = parseBlock<TEvent>(buffer)
    if (last !== undefined) onEvent(last)
  } finally {
    reader.releaseLock()
  }
}

/** The next piece of the body; null once it has ended or the reader was stopped. A dropped connection is the usual error. */
const readChunk = async (
  reader: ReadableStreamDefaultReader<string>,
  url: string,
  signal: AbortSignal | undefined,
): Promise<string | null> => {
  try {
    const { value, done } = await reader.read()
    return done ? null : value
  } catch {
    if (signal?.aborted) return null
    throw toApiError(undefined, 0, url)
  }
}

/** One event's JSON, from its `data:` lines; undefined for a comment or keep-alive. */
const parseBlock = <TEvent>(block: string): TEvent | undefined => {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trimStart())
    .join('\n')
  return data ? (JSON.parse(data) as TEvent) : undefined
}

const envelopeOf = async (response: Response): Promise<Partial<ApiError> | undefined> => {
  try {
    return (await response.json()) as Partial<ApiError>
  } catch {
    return undefined
  }
}
